import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    FilePlus2,
    Clock,
    CheckCircle2,
    TrendingUp,
    ArrowRight,
    Zap,
    Flame,
    Sun,
    ExternalLink,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SECTORS, SECTOR_LABELS } from "../data/wizardConfig";

const STATUS_META = {
    nuovo: { label: "Nuovo", className: "badge-new" },
    in_valutazione: {
        label: "In valutazione",
        className: "inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full text-xs font-bold",
    },
    risposto: { label: "Preventivo ricevuto", className: "badge-replied" },
};

export default function Dashboard() {
    const { user } = useAuth();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    // Admin and legacy users see all; agents see only what admin allowed
    const allowed = user?.role === "admin" ? null : user?.allowed_sectors ?? null;

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/quotes");
                setQuotes(data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const pending = quotes.filter((q) => q.status !== "risposto").length;
    const replied = quotes.filter((q) => q.status === "risposto").length;

    return (
        <div className="space-y-8" data-testid="agent-dashboard">
            {/* Hero */}
            <div className="relative bg-[#0C4650] text-white rounded-3xl overflow-hidden p-8 sm:p-10">
                <div
                    className="absolute inset-0 opacity-10"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 20% 30%, #C7FF2C 0%, transparent 40%), radial-gradient(circle at 80% 70%, #91C6C0 0%, transparent 40%)",
                    }}
                />
                <div className="relative z-10">
                    <div className="text-[11px] tracking-[0.3em] font-bold text-[#C7FF2C]">
                        CIAO, {user?.name?.split(" ")[0]?.toUpperCase()}
                    </div>
                    <h1 className="mt-2 text-3xl sm:text-5xl font-black leading-tight">
                        Pronto a creare un nuovo preventivo?
                    </h1>
                    <p className="mt-3 text-white/70 max-w-2xl">
                        Scegli il settore, rispondi al wizard guidato e lascia alla sede il compito
                        di costruire l'offerta migliore per il tuo cliente.
                    </p>
                    <Link
                        to="/nuovo-preventivo"
                        className="inline-flex items-center gap-2 mt-6 bg-[#C7FF2C] text-[#0C4650] font-bold px-6 py-3 rounded-lg hover:bg-white transition-all"
                        data-testid="new-quote-cta"
                    >
                        <FilePlus2 className="w-4 h-4" /> Nuovo preventivo
                    </Link>
                </div>
            </div>

            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-4">
                <StatCard
                    icon={<FilePlus2 className="w-5 h-5" />}
                    label="Preventivi totali"
                    value={quotes.length}
                    color="#0C4650"
                />
                <StatCard
                    icon={<Clock className="w-5 h-5" />}
                    label="In lavorazione"
                    value={pending}
                    color="#F59E0B"
                />
                <StatCard
                    icon={<CheckCircle2 className="w-5 h-5" />}
                    label="Offerte ricevute"
                    value={replied}
                    color="#22C55E"
                />
            </div>

            {/* Sectors quick — le aree di lavoro attive */}
            <div className="grid sm:grid-cols-2 gap-4 max-w-4xl">
                {Object.values(SECTORS)
                    .filter((s) => !allowed || allowed.includes(s.key))
                    .map((s) => (
                    <Link
                        key={s.key}
                        to={s.key === "energy_quoter" ? "/energy-quoter" : `/nuovo-preventivo/${s.key}`}
                        className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white hover:-translate-y-1 hover:shadow-lg transition-all flex flex-col"
                        data-testid={`sector-quick-${s.key}`}
                    >
                        <div
                            className="h-40 bg-cover bg-center"
                            style={{ backgroundImage: `url(${s.image})` }}
                        >
                            <div className="h-full bg-gradient-to-tr from-[#0C4650]/80 via-[#0C4650]/40 to-transparent" />
                        </div>
                        <div className="p-5 flex-1 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-[10px] tracking-[0.28em] text-[#898A8D] font-bold">
                                    SETTORE
                                </div>
                                <div className="font-bold text-lg text-[#0C4650] leading-tight">
                                    {s.label}
                                </div>
                                <div className="text-sm text-gray-500 mt-0.5">{s.tagline}</div>
                            </div>
                            <div className="w-10 h-10 shrink-0 rounded-full bg-[#C7FF2C] flex items-center justify-center group-hover:rotate-45 transition-transform">
                                <ArrowRight className="w-4 h-4 text-[#0C4650]" />
                            </div>
                        </div>
                    </Link>
                ))}

                {/* Energy Quoter — smart AI tool */}
                {(!allowed || allowed.includes("energy_quoter")) && (
                <Link
                    to="/energy-quoter"
                    className="group relative overflow-hidden rounded-2xl border-2 border-[#C7FF2C] bg-white hover:-translate-y-1 hover:shadow-xl transition-all flex flex-col"
                    data-testid="sector-quick-energy_quoter"
                >
                    <div className="h-40 relative bg-gradient-to-br from-[#0C4650] via-[#0a3a42] to-[#06252b] overflow-hidden">
                        <div
                            className="absolute inset-0 opacity-30"
                            style={{
                                backgroundImage:
                                    "radial-gradient(circle at 30% 40%, #C7FF2C 0%, transparent 35%), radial-gradient(circle at 70% 70%, #91C6C0 0%, transparent 35%)",
                            }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="relative w-16 h-16 rounded-2xl bg-[#C7FF2C] flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                                <Zap className="w-8 h-8 text-[#0C4650]" strokeWidth={2.5} />
                            </div>
                        </div>
                        <div className="absolute bottom-3 left-3 flex gap-1.5">
                            <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur text-white text-[10px] font-bold px-2 py-1 rounded-full">
                                <Zap className="w-2.5 h-2.5 text-[#C7FF2C]" /> LUCE
                            </span>
                            <span className="inline-flex items-center gap-1 bg-white/10 backdrop-blur text-white text-[10px] font-bold px-2 py-1 rounded-full">
                                <Flame className="w-2.5 h-2.5 text-[#C7FF2C]" /> GAS
                            </span>
                        </div>
                    </div>
                    <div className="p-5 flex-1 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-[10px] tracking-[0.28em] text-[#0C4650] font-black">
                                ⚡ SMART TOOL · AI
                            </div>
                            <div className="font-bold text-lg text-[#0C4650] leading-tight">
                                Energy Quoter
                            </div>
                            <div className="text-sm text-gray-500 mt-0.5">
                                Analisi bolletta AI, switch con risparmio
                            </div>
                        </div>
                        <div className="w-10 h-10 shrink-0 rounded-full bg-[#C7FF2C] flex items-center justify-center group-hover:rotate-45 transition-transform">
                            <ArrowRight className="w-4 h-4 text-[#0C4650]" />
                        </div>
                    </div>
                </Link>
                )}

            </div>

            {allowed && allowed.length === 0 && (
                <div className="card p-6 border-l-4 border-amber-400 bg-amber-50/50" data-testid="no-sectors-alert">
                    <div className="font-bold text-[#0C4650]">Nessun settore abilitato</div>
                    <p className="text-sm text-gray-600 mt-1">
                        Il tuo profilo non ha ancora settori attivi. Contatta l'amministratore per richiedere le abilitazioni.
                    </p>
                </div>
            )}

            {/* Recent quotes */}
            <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="text-xl font-bold text-[#0C4650]">Le mie richieste</h2>
                        <p className="text-sm text-gray-500">Ultimi preventivi creati</p>
                    </div>
                    <Link
                        to="/richieste"
                        className="text-sm font-bold text-[#0C4650] hover:text-[#091f26]"
                        data-testid="view-all-quotes"
                    >
                        Vedi tutte →
                    </Link>
                </div>
                {loading ? (
                    <div className="py-12 text-center text-gray-400">Caricamento…</div>
                ) : quotes.length === 0 ? (
                    <div className="py-12 text-center">
                        <TrendingUp className="w-10 h-10 text-[#91C6C0] mx-auto" />
                        <p className="mt-3 text-gray-500">
                            Nessun preventivo ancora. Creane uno in pochi secondi.
                        </p>
                        <Link to="/nuovo-preventivo" className="btn-primary mt-4 inline-flex">
                            <FilePlus2 className="w-4 h-4" /> Crea il primo
                        </Link>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {quotes.slice(0, 5).map((q) => (
                            <Link
                                key={q.id}
                                to={`/richieste/${q.id}`}
                                className="flex items-center justify-between py-3 hover:bg-gray-50 px-3 -mx-3 rounded-lg transition"
                                data-testid={`quote-row-${q.id}`}
                            >
                                <div className="min-w-0">
                                    <div className="font-bold text-[#0C4650] truncate">
                                        {q.client_name}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                        {SECTOR_LABELS[q.sector] || SECTORS[q.sector]?.label} ·{" "}
                                        {new Date(q.created_at).toLocaleDateString("it-IT")}
                                    </div>
                                </div>
                                <span className={STATUS_META[q.status]?.className}>
                                    {STATUS_META[q.status]?.label}
                                </span>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

const StatCard = ({ icon, label, value, color }) => (
    <div className="card p-5 flex items-center gap-4">
        <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: `${color}1a`, color }}
        >
            {icon}
        </div>
        <div>
            <div className="text-xs uppercase tracking-wider text-[#898A8D] font-bold">{label}</div>
            <div className="text-2xl font-black text-[#0C4650]">{value}</div>
        </div>
    </div>
);
